import React, { useState } from 'react';
import {
  createShow,
  defaultShowSeed,
  deleteShow,
  formatShowDate,
  updateShow,
  useShows,
  type ShowInput,
  type ShowRecord,
} from '../lib/shows';

const emptyForm: ShowInput = {
  startsOn: '',
  city: '',
  venue: '',
  location: '',
  note: '',
};

function toForm(show: ShowRecord): ShowInput {
  return {
    startsOn: show.startsOn,
    city: show.city,
    venue: show.venue,
    location: show.location,
    note: show.note ?? '',
  };
}

export function AdminShows() {
  const { shows, loading, error: showsError } = useShows();
  const [form, setForm] = useState<ShowInput>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const saveShow = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      if (editingId) {
        await updateShow(editingId, form);
        setStatus('Show updated.');
      } else {
        await createShow(form);
        setStatus('Show added.');
      }
      setForm(emptyForm);
      setEditingId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save show.');
    } finally {
      setBusy(false);
    }
  };

  const importDefaults = async () => {
    setBusy(true);
    setError(null);
    try {
      for (const show of defaultShowSeed) {
        await createShow(show);
      }
      setStatus('Loaded the current listed shows.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not import shows.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <form className="contact-form admin-form card" onSubmit={saveShow}>
        <p className="eyebrow">{editingId ? 'Edit show' : 'Add a show'}</p>
        <div className="admin-form-grid">
          <label>
            Date*
            <input
              type="date"
              required
              value={form.startsOn}
              onChange={(e) => setForm({ ...form, startsOn: e.target.value })}
            />
          </label>
          <label>
            City
            <input
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder="Roseville, CA"
            />
          </label>
        </div>
        <label>
          Venue*
          <input
            required
            value={form.venue}
            onChange={(e) => setForm({ ...form, venue: e.target.value })}
            placeholder="Goldfield Trading Post"
          />
        </label>
        <label>
          Details
          <input
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            placeholder="Co-headline, festival slot, private event…"
          />
        </label>
        <label>
          Note
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </label>
        {form.startsOn && (
          <p className="meta">
            Public date chip: <strong>{formatShowDate(form.startsOn, form.city)}</strong>
          </p>
        )}
        <div className="form-actions">
          <button className="hero-btn" type="submit" disabled={busy}>
            {editingId ? 'Save changes' : 'Add show'}
          </button>
          {editingId && (
            <button
              className="text-link as-button"
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(emptyForm);
              }}
            >
              Cancel edit
            </button>
          )}
        </div>
      </form>

      {status && <p className="status">{status}</p>}
      {(error || showsError) && <p className="status error">{error || showsError}</p>}

      <div className="admin-list-header">
        <h2>Upcoming shows</h2>
        {!loading && shows.length === 0 && (
          <button className="text-link as-button" type="button" onClick={importDefaults} disabled={busy}>
            Load the current listed shows
          </button>
        )}
      </div>

      {loading ? (
        <p className="meta">Loading shows…</p>
      ) : shows.length === 0 ? (
        <p className="meta">No shows in the console yet. Add one above.</p>
      ) : (
        <div className="admin-show-list">
          {shows.map((show) => (
            <article key={show.id} className="card admin-show-row">
              <div>
                <div className="date-chip">{formatShowDate(show.startsOn, show.city)}</div>
                <h3>{show.venue}</h3>
                <p>{show.location || '—'}</p>
                {show.note && <p className="meta">{show.note}</p>}
              </div>
              <div className="admin-row-actions">
                <button
                  className="text-link as-button"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setEditingId(show.id);
                    setForm(toForm(show));
                    setStatus(null);
                    setError(null);
                  }}
                >
                  Edit
                </button>
                <button
                  className="text-link as-button"
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    if (!window.confirm(`Delete ${show.venue}?`)) return;
                    setBusy(true);
                    setError(null);
                    try {
                      await deleteShow(show.id);
                      if (editingId === show.id) {
                        setEditingId(null);
                        setForm(emptyForm);
                      }
                      setStatus('Show deleted.');
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Could not delete show.');
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}
